import { Injectable } from '@angular/core';
import { catchError, Observable, of, take } from 'rxjs';
import { GeneralService } from './general.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  AccountStatement,
  AssetDocument,
  ClientsActivePortfolio,
  Comments,
  ConceptCancel,
  DepositDetail,
  DepositsActivePortfolio,
  DepositsConcept,
  FacturaDetalle,
  Finishes,
  ResidualTable,
  FondeadoresActivePortfolio,
  ListActivePortfolio,
  OtherCharges,
  PaymentActivePortfolio,
  PendingRent,
  PortfolioSummary,
  QuotationDocument,
  responseDocument,
  RightsAssignment,
  FinishesSaleValuesPayload,
} from '../models/active-portfolio-model/active-portfolio.model';
import { GeneralDTO } from '../models/components-general/general-dto.model';
import { ResultDTO } from '../models/components-general/result-dto.model';

@Injectable({
  providedIn: 'root',
})
export class ActivePortfolioService {
  public URL_API = environment.apiURLNew;

  constructor(
    private http: HttpClient,
    private generalService: GeneralService,
  ) {}

  public getActivePortfolio(body: {
    AnchorerId: null | number;
    CustomerId: null | number;
    DealerId: null | number;
    DescriptionState: null | string;
    IsAriza: null | number;
    QuotationId: null | number;
    Search: null | string;
  }): Observable<ListActivePortfolio[] | null> {
    const params = this.generalService.buildParams(body);
    return this.http
      .get<ListActivePortfolio[]>(this.URL_API + '/GetActivePortfolio', { params })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getActivePortFolioDepositDetail(
    quotationId: string | number,
  ): Observable<DepositsActivePortfolio | null> {
    return this.http
      .get<DepositsActivePortfolio>(
        this.URL_API + `/GetActivePortFolioDepositDetail/${quotationId}`,
        {},
      )
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public cancelMassivePayments(body: {
    comment: string;
    idsPagos: string;
    quotationId: number;
    userId: number;
  }): Observable<any> | null {
    return this.http.post<any>(this.URL_API + `/CancelMassivePayments`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public getActivePortFolioDetail(
    quotationId: string | number,
  ): Observable<PaymentActivePortfolio[] | null> {
    return this.http
      .get<PaymentActivePortfolio[]>(this.URL_API + `/GetActivePortFolioDetail/${quotationId}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getSpInformationActivePortfolio(
    quotationId: string | number,
  ): Observable<PortfolioSummary[] | null> {
    return this.http
      .get<PortfolioSummary[]>(this.URL_API + `/GetSpInformationActivePortfolio/${quotationId}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getClients(): Observable<ClientsActivePortfolio[] | null> {
    return this.http.get<ClientsActivePortfolio[]>(this.URL_API + `/GetClients`, {}).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public getFondeadores(): Observable<FondeadoresActivePortfolio[] | null> {
    return this.http.get<FondeadoresActivePortfolio[]>(this.URL_API + `/GetFondeadores`, {}).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  // Facturas

  public getFacturasCarteraActiva(
    idCotizacion: number | string,
  ): Observable<DepositDetail[] | null> {
    return this.http
      .get<DepositDetail[]>(this.URL_API + `/GetFacturasCarteraActiva/${idCotizacion}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getFacturasCarteraActivaDetail(
    idCotizacion: number | string,
    invoices: number | string,
  ): Observable<FacturaDetalle[] | null> {
    return this.http
      .get<
        FacturaDetalle[]
      >(this.URL_API + `/GetFacturasCarteraActivaDetail/${idCotizacion}/${invoices}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getAccountStatement(
    ID_CLIENTE: number | string,
    ID_COTIZACION: number | string,
  ): Observable<AccountStatement | null> {
    const params = this.generalService.buildParams({ ID_CLIENTE, ID_COTIZACION });
    return this.http.get<AccountStatement>(this.URL_API + `/GetAccountStatement`, { params }).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public getMigCancelableConceptByQuotationResults(
    ID_COTIZACION: number | string,
  ): Observable<ConceptCancel[] | null> {
    return this.http
      .get<
        ConceptCancel[]
      >(this.URL_API + `/GetMigCancelableConceptByQuotationResults/${ID_COTIZACION}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getMigCancelItemizedConcepts(
    idCotizacion: number | string,
    status?: string | null,
  ): Observable<DepositsConcept[] | null> {
    const params = this.generalService.buildParams({ status });
    return this.http
      .get<
        DepositsConcept[]
      >(this.URL_API + `/GetMigCancelItemizedConcepts/${idCotizacion}`, { params })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public cancelMigSingleConcept(
    idUsuario: number | string,
    body: {
      comentario: string;
      idCotizacion: number;
      idsCartera: number[];
    },
  ): Observable<AccountStatement | null> {
    return this.http
      .post<AccountStatement>(this.URL_API + `/CancelMigSingleConcept/${idUsuario}`, body)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public cancelMigMultipleConcepts(body: {
    comentario: string;
    idConcepto: number;
    idCotizacion: number;
    idUsuario: number;
  }): Observable<AccountStatement | null> {
    return this.http.post<AccountStatement>(this.URL_API + `/CancelMigMultipleConcepts`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public getQuotationDocuments(body: {
    desStatus: string;
    idCaratula?: number;
    idCotizacion: number;
    inventario: number;
  }): Observable<QuotationDocument[]> {
    const params = this.generalService.buildParams(body);
    return this.http
      .get<QuotationDocument[]>(this.URL_API + `//MIG_GetQuotationDocuments`, { params })
      .pipe(
        take(1),
        catchError(() => {
          return of([]);
        }),
      );
  }

  public getAssetDocuments(body: { idActivo: number }): Observable<AssetDocument[]> {
    const params = this.generalService.buildParams(body);
    return this.http.get<AssetDocument[]>(this.URL_API + `/GetAssetDocuments`, { params }).pipe(
      take(1),
      catchError(() => {
        return of([]);
      }),
    );
  }

  public documentCopy(body: {
    desStatus: string;
    idCaratula?: number;
    idCotizacion?: number;
    idActivo?: number;
    idUsuario: number;
    parent: number | null;
  }): Observable<responseDocument | null> {
    return this.http
      .post<responseDocument>(this.URL_API + `/MIG_AddQuotationDocumentCopy`, body)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public documentAssetCopy(body: {
    idActivo?: number;
    idUsuario: number;
    parent: number | null;
  }): Observable<responseDocument | null> {
    return this.http.post<responseDocument>(this.URL_API + `/AddAssetDocumentCopy`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public deleteAssetCopy(body: {
    idActivo: number;
    idUsuario: number;
    idActivoDocumento: number | null;
  }): Observable<responseDocument | null> {
    return this.http.post<responseDocument>(this.URL_API + `/DeleteAssetDocumentCopy`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public deleteCopy(body: {
    desStatus: string;
    idCaratula?: number;
    idCotizacion?: number;
    idUsuario: number;
    idActivo?: number;
    idCotizacionDocumento: number | null;
  }): Observable<responseDocument | null> {
    return this.http
      .post<responseDocument>(this.URL_API + `/MIG_DeleteQuotationDocumentCopy`, body)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getMobileUser(quotationId: number): Observable<ResultDTO | null> {
    return this.http
      .post<ResultDTO>(this.URL_API + `/api/Mobile/GetUser?quotationId=${quotationId}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public createMobileUser(quotationId: number): Observable<ResultDTO | null> {
    return this.http
      .post<ResultDTO>(this.URL_API + `/api/Mobile/CreateUser?quotationId=${quotationId}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public updateMobileUser(quotationId: number): Observable<ResultDTO | null> {
    return this.http
      .post<ResultDTO>(this.URL_API + `/api/Mobile/UpdateUser?quotationId=${quotationId}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getOtherCharges(quotationId: string | number): Observable<OtherCharges[] | null> {
    return this.http
      .get<
        OtherCharges[]
      >(this.URL_API + `/api/Quotation/GetOtherCharges?quotationId=${quotationId}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getComments(quotationId: string | number): Observable<Comments[] | null> {
    return this.http
      .get<Comments[]>(this.URL_API + `/api/Comments?quotationId=${quotationId}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public addComment(
    quotationId: number,
    comment: string,
    userId: number,
  ): Observable<Comments[] | null> {
    const params = new HttpParams()
      .set('quotationId', quotationId.toString())
      .set('comment', comment)
      .set('userIdDynamic', userId.toString());

    return this.http.post<Comments[]>(`${this.URL_API}/api/Comments/Insert`, {}, { params }).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public assignRight(body: {
    quotationId: number;
    clientId: number | null;
    assignDate: string | null;
  }): Observable<ResultDTO | null> {
    return this.http.post<ResultDTO>(this.URL_API + `/MigAssignRights`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public validateRightsAssignment(body: {
    quotationId: number;
    clientId: number | null;
    assignDate: string | null;
  }): Observable<ResultDTO | null> {
    return this.http.post<ResultDTO>(this.URL_API + `/ValidateRightsAssignment`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public updateRightsAssignment(body: {
    quotationId: number;
    clientId: number | null;
    assignDate: string | null;
  }): Observable<ResultDTO | null> {
    return this.http.post<ResultDTO>(this.URL_API + `/UpdateRightsAssignment`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public cancelRightsAssignment(body: {
    quotationId: number;
    clientId: number | null;
    assignDate: string | null;
  }): Observable<ResultDTO | null> {
    return this.http.post<ResultDTO>(this.URL_API + `/CancelRightsAssignment`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public getRightsAssignment(quotationId: number): Observable<RightsAssignment | null> {
    return this.http
      .get<RightsAssignment>(this.URL_API + `/MigGetRightsAssignment?quotationId=${quotationId}`)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getPendingRents(quotationId: number): Observable<PendingRent[] | null> {
    return this.http
      .get<PendingRent[]>(this.URL_API + `/GetPendingRents?quotationId=${quotationId}`)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getFinishes(quotationId: string | number): Observable<Finishes[] | null> {
    return this.http
      .get<Finishes[]>(this.URL_API + `/api/Finishes/GetFinishes/${quotationId}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public upsertFinishesSaleValues(body: FinishesSaleValuesPayload): Observable<ResultDTO | null> {
    return this.http
      .post<ResultDTO>(this.URL_API + `/api/Finishes/UpsertFinishesSaleValues`, body)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getResidualTable(quotationId: string | number): Observable<ResidualTable[] | null> {
    return this.http
      .get<ResidualTable[]>(this.URL_API + `/api/Finishes/GetResidualTable/${quotationId}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }
}
